import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { database } from '../firebase';
import { ref as dbRef, get, update } from 'firebase/database';
import { normalizeLanguage } from '../utils/languagePreference';

const rtlLanguages = ['ar', 'he', 'fa', 'ur'];

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState(normalizeLanguage(i18n.language) || 'en');
    const [isRTL, setIsRTL] = useState(normalizeLanguage(i18n.language) === 'ar');
    const [isLoading, setIsLoading] = useState(true);
    const preferenceRevision = useRef(0);
    const accountLoads = useRef(new Map());

    // Apply RTL/LTR direction to document
    const applyDirection = useCallback((lang) => {
        lang = normalizeLanguage(lang) || 'en';
        const rtl = rtlLanguages.includes(lang);
        document.documentElement.dir = rtl ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
        setIsRTL(rtl);

        // Also update body class for additional styling hooks
        document.body.classList.toggle('rtl', rtl);
        document.body.classList.toggle('ltr', !rtl);
    }, []);

    // Change language handler
    const changeLanguage = useCallback(async (lang, companyId = null) => {
        lang = normalizeLanguage(lang);
        if (!lang) return false;
        preferenceRevision.current += 1;
        try {
            // Save before asynchronous work so route reloads and pending account
            // reads cannot replace the user's current choice.
            localStorage.setItem('i18nextLng', lang);
            // Update i18next
            await i18n.changeLanguage(lang);

            // Update local storage
            localStorage.setItem('i18nextLng', lang);

            // Apply direction
            applyDirection(lang);
            setLanguage(lang);

            // Persist to Firebase if companyId provided
            if (companyId) {
                try {
                    await update(dbRef(database, `companies/${companyId}/settings`), {
                        language: lang
                    });
                } catch (err) {
                    console.error('Error saving language preference to Firebase:', err);
                }
            }

            return true;
        } catch (error) {
            console.error('Error changing language:', error);
            return false;
        }
    }, [i18n, applyDirection]);

    // Load language preference from Firebase
    const loadLanguagePreference = useCallback(async (companyId) => {
        if (!companyId) return;
        // A personal choice takes precedence over company-wide defaults.
        if (normalizeLanguage(localStorage.getItem('i18nextLng'))) return;
        if (accountLoads.current.has(companyId)) return accountLoads.current.get(companyId);
        const revision = preferenceRevision.current;
        const pending = (async () => { try {
            const settingsRef = dbRef(database, `companies/${companyId}/settings`);
            const snapshot = await get(settingsRef);

            if (snapshot.exists()) {
                const settings = snapshot.val();
                if (normalizeLanguage(settings.language) &&
                    preferenceRevision.current === revision &&
                    !normalizeLanguage(localStorage.getItem('i18nextLng'))) {
                    await changeLanguage(settings.language);
                }
            }
        } catch (error) {
            accountLoads.current.delete(companyId);
            console.error('Error loading language preference:', error);
        } })();
        accountLoads.current.set(companyId, pending);
        return pending;
    }, [changeLanguage]);

    // Initialize direction on mount
    useEffect(() => {
        const currentLang = normalizeLanguage(i18n.language) || 'en';
        applyDirection(currentLang);
        setLanguage(currentLang);
        setIsLoading(false);
    }, [i18n.language, applyDirection]);

    // Listen for language changes from i18next
    useEffect(() => {
        const handleLanguageChanged = (lang) => {
            applyDirection(lang);
            setLanguage(normalizeLanguage(lang) || 'en');
        };

        i18n.on('languageChanged', handleLanguageChanged);

        return () => {
            i18n.off('languageChanged', handleLanguageChanged);
        };
    }, [i18n, applyDirection]);

    const value = {
        language,
        isRTL,
        isLoading,
        changeLanguage,
        loadLanguagePreference,
        supportedLanguages: [
            { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
            { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇱🇧' }
        ]
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
